import React from "react"
import { Script, graphql } from "gatsby"
import { Layout } from "@bw/layouts"
import { Seo } from "@bw/bits"
import { Breadcrumbs, SidebarLayout } from "@bw/partials"
import { Content, Footer, Header } from "../components/modules/mapModules.jsx"

export const Head = ({ data }) => (
  <>
    {data.collectionPages.head && (
      <Script
        id={`user-script-${data.collectionPages.id}`}
        dangerouslySetInnerHTML={{ __html: data.collectionPages.head }}
      />
    )}
  </>
)

const PageTemplate = ({ data, pageContext, location }) => {
  const { collectionPages, sidemenu, parents } = data
  const page = collectionPages

  if (pageContext.hasSiblings && sidemenu?.nodes) {
    // ensure that the sorting is right
    sidemenu.nodes.sort(
      (a, b) =>
        pageContext.siblings.indexOf(a.statamicId) -
        pageContext.siblings.indexOf(b.statamicId)
    )
  }

  const breadcrumbsLinks = [
    {
      title: "Accueil",
      to: "/",
    },
  ]

  if (pageContext.hasParents && parents?.nodes) {
    // ensure that the sorting is right
    parents.nodes.sort(
      (a, b) =>
        pageContext.parents.indexOf(a.statamicId) -
        pageContext.parents.indexOf(b.statamicId)
    )
    parents.nodes.forEach(bread => breadcrumbsLinks.push(bread))
  }

  breadcrumbsLinks.push({
    title: page.title,
    to: page.slug,
  })

  if (typeof page === "undefined") {
    return null
  }

  const content = <Content modules={page.content} />

  return (
    <Layout color={page.color?.value} {...{ pageContext }}>
      <Seo
        url={location.href}
        title={page.metaTitle || page.title}
        description={page.metaDescription}
        image={page.image?.url}
        meta={[
          {
            name: "robots",
            content: page.doNotIndex
              ? "noindex, nofollow"
              : "max-image-preview:large, index, follow",
          },
        ]}
      />
      <Header modules={page.header} page={page} />
      {breadcrumbsLinks[1].title.toLowerCase() === "accueil" ? null : (
        <Breadcrumbs breadcrumbs={breadcrumbsLinks} />
      )}
      {sidemenu && sidemenu.nodes && sidemenu.nodes.length > 0 ? (
        <SidebarLayout sidemenu={sidemenu.nodes}>{content}</SidebarLayout>
      ) : (
        content
      )}
      <Footer modules={page.footer} />
    </Layout>
  )
}

export default PageTemplate

export const pageQuery = graphql`
  query pageQuery(
    $id: String!
    $siblings: [String]
    $hasSiblings: Boolean = false
    $parents: [String]
    $hasParents: Boolean = false
  ) {
    parents: allCollectionPages(filter: { statamicId: { in: $parents } }) {
      nodes @include(if: $hasParents) {
        statamicId
        to: slug
        title
      }
    }

    sidemenu: allCollectionPages(filter: { statamicId: { in: $siblings } }) {
      nodes @include(if: $hasSiblings) {
        statamicId
        slug
        title
      }
    }

    collectionPages(id: { eq: $id }) {
      id
      head
      title
      metaTitle
      metaDescription
      color {
        value
      }
      pictogram {
        value
      }
      image {
        url
      }
      doNotIndex

      teaserImage {
        localImage {
          childImageSharp {
            gatsbyImageData(width: 1668, height: 275)
          }
        }
      }
      header {
        __typename
        suptitle
        title
        subtitle

        ... on PageHeaderModule {
          ...PageHeaderModuleFragment
        }
        ... on HeroModule {
          ...HeroModuleFragment
        }
      }
      content {
        __typename
        suptitle
        title
        subtitle
        buttonType {
          value
        }
        buttonUrl
        buttonIsExternal
        buttonText
        buttonPosition {
          value
        }
        buttonEntry {
          url
        }
        ancre

        ... on CallToActionModule {
          ...CallToActionModuleFragment
        }
        ... on ContentModule {
          ...ContentModuleFragment
        }
        ... on ContentWithImageModule {
          ...ContentWithImageModuleFragment
        }
        ... on GalleryModule {
          ...GalleryModuleFragment
        }
        ... on ContentOnColumnsModule {
          ...ContentOnColumnsModuleFragment
        }
        ... on TeasersLatestModule {
          ...TeasersLatestModuleFragment
        }
        ... on TeasersCustomModule {
          ...TeasersCustomModuleFragment
        }
        ... on TeasersManualModule {
          ...TeasersManualModuleFragment
        }
        ... on LogoGridModule {
          ...LogoGridModuleFragment
        }
        ... on ImageModule {
          ...ImageModuleFragment
        }
        ... on VideoModule {
          ...VideoModuleFragment
        }
        ... on FormModule {
          ...FormModuleFragment
        }
        ... on MinusModule {
          ...MinusModuleFragment
        }
        ... on DownloadsModule {
          ...DownloadsModuleFragment
        }
        ... on MagazinesModule {
          ...MagazinesModuleFragment
        }
      }
      footer {
        __typename
        suptitle
        title
        subtitle
        buttonType {
          value
        }
        buttonUrl
        buttonIsExternal
        buttonText
        buttonPosition {
          value
        }
        buttonEntry {
          url
        }
      }
    }
  }
`
